import React from "react";
import { Box } from "@mui/material";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { useImageColumns } from "./data";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";
import { useTranslate } from "src/locales/use-locales";
import { referenceService } from "src/api/references/referenceService";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ImageIcon from "@mui/icons-material/Image";
import { formatDateTime } from "src/utils/date/dateHelper";
import { BradenFileRow, BradenFileApiResponse, BradenFileGetResponse } from "./types";

interface ImagePanelProps {
  prsdId: number;
  ptId: number;
  offId: number;
  mnypsdId?: number;
  mpsrbdId?: number;
  extraAccess?: boolean;
  onUpload?: (file: File) => Promise<void>;
  onUpdate?: (id: number, file: File) => Promise<void>;
}

const ImagePanel: React.FC<ImagePanelProps> = ({
  prsdId,
  ptId,
  offId,
  mnypsdId,
  mpsrbdId,
  extraAccess,
  onUpload,
  onUpdate
}) => {
  const { t } = useTranslate();
  const [rows, setRows] = React.useState<BradenFileRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [alertType, setAlertType] = React.useState<"success" | "error">("success");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const updateFileInputRef = React.useRef<HTMLInputElement>(null);

  const fetchImages = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = (await referenceService.mcBradenFile.list(
        prsdId
      )) as unknown as BradenFileApiResponse;
      const isSuccess = response.issuccess ?? response.success ?? false;
      const data = response.data;

      if (isSuccess && Array.isArray(data)) {
        setRows(
          data.map((item) => {
            const dateValue = item.createddate || item.createdDate || "";
            return {
              id: item.id,
              imageName: item.name,
              addedDate: formatDateTime(dateValue),
              createdUser: item.createduser || item.createdUser || "",
            };
          })
        );
      }
    } catch (error) {
      console.error("Error fetching images:", error);
    } finally {
      setLoading(false);
    }
  }, [prsdId]);

  React.useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      if (onUpload) {
        await onUpload(file);
      } else {
        await referenceService.mcBradenFile.upload({
          prsdId,
          ptId,
          offId,
          mnypsdId,
          mpsrbdId,
          file
        });
      }
      await fetchImages();
    } catch (error) {
      console.error("Error uploading file:", error);
      const errorMessage = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (error as { message?: string })?.message
        || t("common.error");
      setAlertMessage(errorMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedId) return;

    try {
      setLoading(true);
      if (onUpdate) {
        await onUpdate(selectedId, file);
      } else {
        await referenceService.mcBradenFile.update(selectedId, {
          ptId,
          offId,
          file
        });
      }
      await fetchImages();
    } catch (error) {
      console.error("Error updating file:", error);
      const errorMessage = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (error as { message?: string })?.message
        || t("common.error");
      setAlertMessage(errorMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
      setSelectedId(null);
      if (updateFileInputRef.current) updateFileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      await referenceService.mcBradenFile.delete(id);
      await fetchImages();
    } catch (error) {
      console.error("Error deleting file:", error);
      const errorMessage = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (error as { message?: string })?.message
        || t("common.error");
      setAlertMessage(errorMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async (id: number) => {
    try {
      setLoading(true);
      const response = await referenceService.mcBradenFile.get(id) as unknown as BradenFileGetResponse;
      const isSuccess = response.issuccess ?? response.success ?? false;

      if (isSuccess && response.data) {
        const byteString = atob(response.data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: "image/png" });
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      const errorMessage = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        || (error as { message?: string })?.message
        || t("common.error");
      setAlertMessage(errorMessage);
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const columns = useImageColumns({
    onOpen: handleOpen,
    onDelete: handleDelete,
    onChange: (id) => {
      setSelectedId(id);
      updateFileInputRef.current?.click();
    },
    extraAccess: Boolean(extraAccess)
  });

  return (
    <Box
      sx={{
        padding: "20px",
        width: "100%",
        maxHeight: "90vh",
        overflow: "auto",
        position: "relative"
      }}
    >
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleUpload}
        accept=".jpg,.jpeg,.png,.gif,.bmp"
      />
      <input
        type="file"
        ref={updateFileInputRef}
        style={{ display: "none" }}
        onChange={handleUpdate}
        accept=".jpg,.jpeg,.png,.gif,.bmp"
      />

      <Box mb={2} display="flex" justifyContent="flex-start">
        <ByzMuiButton
          onClick={() => fileInputRef.current?.click()}
          icon={<ImageIcon />}
          loading={loading}
          disabled={!extraAccess}
        >
          {t("common.add-visual")}
        </ByzMuiButton>
      </Box>

      <ByzMuiDataGrid
        loading={loading}
        customToolbar={ByzDataGridToolbar}
        columns={columns}
        pageSize={10}
        rows={rows}
      />

      <ByzMuiSnackBar
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        title={alertType === "success" ? t("common.success") : t("common.error")}
        message={alertMessage}
        type={alertType}
      />
    </Box>
  );
};

export default ImagePanel;
